import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RENTALS_QUERY_KEYS } from "@/features/rentals/configs/rentalsQueryKeys.config";
import {
  cancelRentalBooking,
  createRentalBooking,
  initiateRentalBookingPayment,
  updateRentalSubsidyChoices,
} from "@/features/rentals/services/rentals.service";
import type {
  CreateBookingPayload,
  CreateVoyageBookingPayload,
  GetRentalBookingsResponse,
  GetRentalCartResponse,
  RentalBooking,
  RentalCartBookingSummary,
  RentalPaymentMethod,
} from "@/features/rentals/types/rentals.type";

const toCartSummary = (booking: RentalBooking): RentalCartBookingSummary => ({
  id: booking.id,
  reference: booking.reference,
  status: booking.status,
  payment_status: booking.payment_status ?? null,
  quantity: booking.quantity,
  start_date: booking.start_date,
  end_date: booking.end_date,
  unit_price_cents: booking.unit_price_cents,
  total_price_cents: booking.total_price_cents,
  subsidy_amount_cents: booking.subsidy_amount_cents ?? null,
  deposit_amount_cents: booking.deposit_amount_cents ?? null,
  notes: booking.notes,
  price_tier: booking.price_tier ?? null,
  voyage_lines: booking.voyage_lines ?? [],
  item: {
    id: booking.item?.id ?? booking.item_prefix_id,
    name: booking.item?.name ?? null,
    kind: booking.item?.kind ?? null,
    mode: booking.item?.mode ?? null,
    image_url: booking.item?.images[0]?.small_url ?? booking.item?.images[0]?.url ?? null,
  },
});

export const useRentalBookingMutations = () => {
  const queryClient = useQueryClient();

  // `refetchType: "all"` — force la refetch même si l'observer
  // (UnifiedCartContext) n'est pas le composant en avant-plan. Sans
  // ça, retour catalogue post-checkout pouvait laisser un booking
  // déjà confirmé visible côté panier le temps que la refetch arrive.
  const invalidateMyBookings = () =>
    queryClient.invalidateQueries({
      queryKey: RENTALS_QUERY_KEYS.myBookings,
      refetchType: "all",
    });
  const invalidateBooking = (bookingId: string) =>
    queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.booking(bookingId) });
  const invalidateCart = () =>
    queryClient.invalidateQueries({
      queryKey: RENTALS_QUERY_KEYS.cart,
      refetchType: "all",
    });
  // Une réservation créée ou retirée change le reste des quotas par saison
  // de toute sa collection, pas seulement de son article.
  const invalidateSeasonQuotaUsage = () =>
    queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.seasonQuotaUsageAll });

  const create = useMutation({
    mutationFn: (payload: CreateBookingPayload | CreateVoyageBookingPayload) => createRentalBooking(payload),
    onSuccess: (response) => {
      const { booking } = response;

      // La création renvoie déjà le booking complet. On l'injecte dans
      // les deux caches utilisés par le panier pour qu'il apparaisse
      // immédiatement, sans attendre les refetchs réseau ci-dessous.
      queryClient.setQueryData<GetRentalBookingsResponse>(
        RENTALS_QUERY_KEYS.myBookings,
        (current) => ({
          bookings: [
            booking,
            ...(current?.bookings ?? []).filter((candidate) => candidate.id !== booking.id),
          ],
        }),
      );
      if (booking.status === "open") {
        queryClient.setQueryData<GetRentalCartResponse>(
          RENTALS_QUERY_KEYS.cart,
          (current) => {
            if (!current) return current;
            const alreadyPresent = current.cart.bookings.some((candidate) => candidate.id === booking.id);
            if (alreadyPresent) return current;

            return {
              cart: {
                ...current.cart,
                bookings: [...current.cart.bookings, toCartSummary(booking)],
                bookings_count: current.cart.bookings_count + 1,
                total_price_cents: current.cart.total_price_cents + (booking.total_price_cents ?? 0),
                remaining_amount_cents:
                  current.cart.remaining_amount_cents + (booking.remaining_amount_cents ?? 0),
              },
            };
          },
        );
      }
      // Pas de refetch immédiat de la liste : la réponse de création
      // est la donnée la plus fraîche et vient d'être mise en cache.
      // Un refetch concurrent pouvait la remplacer temporairement par
      // l'ancienne liste et faire réapparaître un panier vide.
      invalidateCart();
      invalidateSeasonQuotaUsage();
      queryClient.invalidateQueries({ queryKey: ["rentals", "items"] });
      const itemId = response.booking.item?.id;
      if (itemId) {
        queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.item(itemId) });
      }
    },
    // Un refus « Autre réservation sur la période » veut dire que le
    // datepicker affichait des dates que l'API ne reconnaît plus. On
    // invalide la fiche et ses dates - même préfixe de clé - pour que
    // le créneau disparaisse tout de suite, sans attendre que le
    // bénéficiaire referme et rouvre la fiche.
    onError: (_err, payload) => {
      queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.item(payload.booking.catalog_item_id) });
    },
  });

  const cancel = useMutation({
    mutationFn: ({ bookingId, comment }: { bookingId: string, comment?: string, optimistic?: boolean }) =>
      cancelRentalBooking(bookingId, comment),
    onMutate: async ({ bookingId, optimistic }) => {
      if (!optimistic) return undefined;
      await queryClient.cancelQueries({ queryKey: RENTALS_QUERY_KEYS.myBookings });
      const previous = queryClient.getQueryData<GetRentalBookingsResponse>(RENTALS_QUERY_KEYS.myBookings);
      const removed = previous?.bookings.find((booking) => booking.id === bookingId);
      queryClient.setQueryData<GetRentalBookingsResponse>(RENTALS_QUERY_KEYS.myBookings, (current) =>
        current ? { ...current, bookings: current.bookings.filter((booking) => booking.id !== bookingId) } : current);
      return { removed };
    },
    onError: (_error, _variables, context) => {
      const removed = context?.removed;
      if (!removed) return;
      queryClient.setQueryData<GetRentalBookingsResponse>(RENTALS_QUERY_KEYS.myBookings, (current) => {
        if (!current || current.bookings.some((booking) => booking.id === removed.id)) return current;
        return { ...current, bookings: [...current.bookings, removed] };
      });
    },
    // `onSettled` (au lieu de `onSuccess`) pour que le UI se
    // rafraîchisse aussi quand le backend renvoie 422 (ex. booking
    // déjà annulé via une autre session, ou cancelled out-of-band
    // côté admin) — sinon le bénéficiaire reste coincé avec un
    // panier qui affiche un booking obsolète et le bouton trash
    // continue de retourner 422.
    onSettled: async (_data, _err, { bookingId }) => {
      await Promise.all([
        invalidateMyBookings(),
        invalidateCart(),
        invalidateBooking(bookingId),
        invalidateSeasonQuotaUsage(),
        queryClient.invalidateQueries({ queryKey: ["rentals", "items"] }),
      ]);
    },
  });

  // Initiate: redirects to Paybox (`credit_card`), confirms immediately
  // if free, or confirms the booking out of cart while leaving payment
  // unpaid (`pay_later`). The booking is invalidated so detail/list
  // pages pick up the new state (`payment_status: pending` for paid
  // flow, full post-confirm state for free / pay_later flows).
  const initiatePayment = useMutation({
    mutationFn: ({ bookingId, paymentMethod, shippingAddressId }: {
      bookingId: string
      paymentMethod?: RentalPaymentMethod
      shippingAddressId?: number | null
    }) => initiateRentalBookingPayment(bookingId, paymentMethod, shippingAddressId),
    onSuccess: (_response, { bookingId }) => {
      invalidateMyBookings();
      invalidateCart();
      invalidateBooking(bookingId);
    },
  });

  const updateSubsidyChoices = useMutation({
    mutationFn: ({ bookingId, choices }: {
      bookingId: string
      choices: { employee?: boolean, beneficiaries?: Record<string, boolean> }
    }) => updateRentalSubsidyChoices(bookingId, choices),
    onSuccess: (_response, { bookingId }) => {
      invalidateBooking(bookingId);
      invalidateCart();
    },
  });

  return {
    create,
    cancel,
    initiatePayment,
    updateSubsidyChoices,
  };
};
